/**
 * Flat-shading tables for the 3Space polygon renderer
 * (docs/formats/shading-and-paint.md, VERIFIED by disassembly + live capture).
 *
 * A face's fill colour is a direct VGA palette index. The renderer darkens or
 * lightens it along its palette ramp by adding a small signed delta chosen from
 * two tables:
 *
 * - `SHADES.DAT` (256 raw bytes, `VOLUME.002`) gives, per palette index, the
 *   **half-width** `n` of the shading span available at that index. `n === 0`
 *   means "flat, never shaded"; otherwise the reachable span is exactly
 *   `[c - n, c + n - 1]`, which lands inside that index's ramp in `BARON.PAL`
 *   for all 26 non-zero entries with zero exceptions.
 * - An 8x32 table of signed deltas baked into both executables' code segments
 *   (`RB.EXE` +0x407BB, `PS.EXE` +0x349BD, byte-identical). Row = `n - 1`,
 *   column = the quantised Lambert term.
 *
 * The renderer only shades a face when `op` has **both** bit 0x80 (filled) and
 * bit 0x20 (shaded) set, and only under VGA; face bytes +3/+4 are the 16-colour
 * EGA/CGA line/fill colours and are dead in the shipped VGA-only build.
 */

import { BinaryReader } from "./binary-reader.ts";

/** `SHADES.DAT` is one half-width byte per palette entry. */
export const SHADES_DAT_LENGTH = 256;
/** Columns of the delta table: the Lambert term quantised to `-16 .. +15`, biased by +16. */
export const SHADE_LEVEL_COUNT = 32;
/** Rows of the delta table, one per half-width `1 .. 8`. */
export const SHADE_ROW_COUNT = 8;
/** Largest half-width `SHADES.DAT` may hold; the engine rejects anything above this and draws flat. */
export const MAX_SHADE_HALF_WIDTH = SHADE_ROW_COUNT;
/** File offset of the delta table inside `RB.EXE` (this build; see `readShadeDeltaTable`). */
export const RB_EXE_SHADE_DELTA_OFFSET = 0x407bb;
/** File offset of the byte-identical copy inside `PS.EXE`. */
export const PS_EXE_SHADE_DELTA_OFFSET = 0x349bd;

const SHADE_DELTA_LENGTH = SHADE_ROW_COUNT * SHADE_LEVEL_COUNT;
const SHADE_LEVEL_BIAS = 16;
const SHADE_LEVEL_MIN = -16;
const SHADE_LEVEL_MAX = 15;
/** Face `op` bit 0x80: the face is a filled polygon rather than a bare polyline. */
export const FACE_OP_FILLED = 0x80;
/** Face `op` bit 0x20: the fill colour is shaded by the face normal. */
export const FACE_OP_SHADED = 0x20;

/**
 * The 8x32 table as it is shipped, transcribed from `RB.EXE` +0x407BB.
 *
 * This is a *transcription of game data into code*, which normally belongs in
 * `data/baked`. It is here because the table is 256 bytes of engine constant
 * that the **browser** needs — `apps/web`'s runtime ramp re-shading walks it
 * every frame — and the alternative was inventing a bake output (and forcing a
 * full rebake) to carry a quarter of a kilobyte that has been byte-identical in
 * both shipped executables since 1990. `readShadeDeltaTable` remains the
 * authority: `tbl-shading.test.ts` reads the real `RB.EXE` and asserts this
 * array equals it byte for byte, so a wrong transcription fails the gate rather
 * than shipping.
 */
export const SHIPPED_SHADE_DELTAS: Int8Array = new Int8Array([
  // n=1
  -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  // n=2
  -2, -2, -2, -2, -2, -2, -2, -2, -2, -2, -1, -1, -1, -1, -1, -1, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  // n=3
  -3, -3, -3, -3, -3, -3, -3, -3, -2, -2, -2, -2, -1, -1, -1, -1, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2,
  // n=4
  -4, -4, -4, -4, -4, -4, -3, -3, -3, -3, -2, -2, -2, -2, -1, -1, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3,
  // n=5
  -5, -5, -5, -5, -5, -5, -4, -4, -3, -3, -2, -2, -1, -1, -1, -1, 0, 0, 1, 1, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4,
  // n=6
  -6, -6, -6, -6, -6, -6, -5, -5, -4, -4, -3, -3, -2, -2, -1, -1, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 5, 5, 5, 5,
  // n=7
  -7, -7, -7, -7, -7, -6, -6, -5, -4, -4, -3, -3, -2, -2, -1, -1, 0, 0, 1, 1, 2, 2, 3, 3, 4, 5, 5, 6, 6, 6, 6, 6,
  // n=8
  -8, -8, -8, -8, -8, -7, -7, -6, -5, -5, -4, -3, -3, -2, -1, -1, 0, 0, 1, 2, 2, 3, 4, 4, 5, 6, 6, 7, 7, 7, 7, 7,
]);

/**
 * Decodes `SHADES.DAT` into its 256 half-widths. Raw, uncompressed, no chunk
 * framing. Throws on the two invariants the engine itself relies on: the exact
 * length, and every entry being a usable half-width (`0 .. 8`).
 */
export function decodeShadesDat(source: ArrayBufferLike | ArrayBufferView): Uint8Array {
  const reader = new BinaryReader(source);
  if (reader.remaining() !== SHADES_DAT_LENGTH) {
    throw new Error(`SHADES.DAT: expected exactly ${SHADES_DAT_LENGTH} bytes, got ${reader.remaining()}`);
  }
  const halfWidths = reader.bytes(SHADES_DAT_LENGTH).slice();
  for (let index = 0; index < SHADES_DAT_LENGTH; index++) {
    const value = halfWidths[index]!;
    if (value > MAX_SHADE_HALF_WIDTH) {
      throw new Error(`SHADES.DAT: entry ${index} is ${value}, above the engine's limit of ${MAX_SHADE_HALF_WIDTH}`);
    }
  }
  return halfWidths;
}

/**
 * Reads the 8x32 signed delta table out of an executable image at `offset`
 * (default: `RB.EXE`'s). Validated structurally rather than by hash so the
 * error names what is wrong: every row must be non-decreasing, start at
 * `-(row + 1)`, and end at `+row` — the shape that keeps `colour + delta`
 * inside the ramp for a half-width of `row + 1`.
 */
export function readShadeDeltaTable(
  source: ArrayBufferLike | ArrayBufferView,
  offset: number = RB_EXE_SHADE_DELTA_OFFSET,
): Int8Array {
  const reader = new BinaryReader(source);
  reader.seek(offset);
  if (reader.remaining() < SHADE_DELTA_LENGTH) {
    throw new Error(`shade delta table: only ${reader.remaining()} bytes available at 0x${offset.toString(16)}`);
  }
  const deltas = new Int8Array(reader.bytes(SHADE_DELTA_LENGTH));
  for (let row = 0; row < SHADE_ROW_COUNT; row++) validateShadeRow(deltas, row, offset);
  return deltas;
}

function validateShadeRow(deltas: Int8Array, row: number, offset: number): void {
  const start = row * SHADE_LEVEL_COUNT;
  const first = deltas[start]!;
  const last = deltas[start + SHADE_LEVEL_COUNT - 1]!;
  const where = `at 0x${offset.toString(16)}`;
  if (first !== -(row + 1) || last !== row) {
    throw new Error(`shade delta table ${where}: row ${row} spans ${first}..${last}, expected ${-(row + 1)}..${row}`);
  }
  for (let column = 1; column < SHADE_LEVEL_COUNT; column++) {
    if (deltas[start + column]! < deltas[start + column - 1]!) {
      throw new Error(`shade delta table ${where}: row ${row} is not monotonic at column ${column}`);
    }
  }
}

/**
 * Quantises a Lambert cosine to the engine's shade level, `0 .. 31`.
 *
 * The original computes `dot(normal, light)` in fixed point — the normal is
 * Q11 (1.0 = 2048), the light vector has magnitude 512 — keeps only the high
 * word of the 32-bit product (an arithmetic shift right by 16, i.e. a floor),
 * folds the single saturating value `+16` down to `+15`, and biases by +16.
 * `floor(16 * cos)` reproduces that exactly for every representable input.
 */
export function shadeLevelFromCosine(cosine: number): number {
  const quantised = Math.floor(cosine * SHADE_LEVEL_BIAS);
  const clamped = Math.min(SHADE_LEVEL_MAX, Math.max(SHADE_LEVEL_MIN, quantised));
  return clamped + SHADE_LEVEL_BIAS;
}

/**
 * The palette index a filled face is actually drawn with: its fill colour
 * moved along its own ramp by the shade delta. Returns `fillColour` unchanged
 * when the face is not shaded (`op` bit 0x20 clear) or when `SHADES.DAT` marks
 * the colour flat — which is what makes index 0, 192 and 208 (the three blacks)
 * and every "special" UI colour immune to shading.
 */
export function shadedPaletteIndex(
  fillColour: number,
  op: number,
  halfWidths: Uint8Array,
  deltas: Int8Array,
  shadeLevel: number,
): number {
  if ((op & FACE_OP_FILLED) === 0 || (op & FACE_OP_SHADED) === 0) return fillColour;
  const halfWidth = halfWidths[fillColour] ?? 0;
  if (halfWidth < 1 || halfWidth > MAX_SHADE_HALF_WIDTH) return fillColour;
  return fillColour + deltas[(halfWidth - 1) * SHADE_LEVEL_COUNT + shadeLevel]!;
}

/**
 * The inclusive span of palette indices a colour can reach under shading:
 * `[c - n, c + n - 1]`, or the single index itself when it is flat. Useful as
 * a bake-time assertion that a resolved colour stays inside its ramp.
 */
export function shadeSpan(colour: number, halfWidths: Uint8Array): { min: number; max: number } {
  const halfWidth = halfWidths[colour] ?? 0;
  if (halfWidth < 1) return { min: colour, max: colour };
  return { min: colour - halfWidth, max: colour + halfWidth - 1 };
}
